import { useEffect } from 'react';

/**
 * @file ONE OWNER FOR `loop.setTimeScale`. A reference count, not a boolean.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BUG THIS CLOSES — "THE LAST ONE TO RESTORE WINS"
 * ─────────────────────────────────────────────────────────────────────────────
 * Every modal overlay in this project freezes the sim the same way: it calls
 * `engine.loop.setTimeScale(0)` when it opens and `setTimeScale(1)` from its own
 * effect cleanup. That is correct for ONE overlay and wrong for two, and
 * `docs/shops.md` records it in exactly those words:
 *
 *   > THREE overlays now freeze the loop and the last one to restore wins.
 *
 * The failure is concrete. A phone CALL freezes the loop and puts a portrait
 * over the street. The player hits a key that opens something over it — or the
 * call ends into a panel, or a cutscene lands on top — and when the INNER
 * overlay unmounts, its cleanup runs `setTimeScale(1)`. The outer overlay is
 * still up, still believing the world is frozen, and the world is now running
 * behind it. Nothing about the picture says so: `setTimeScale` is a write with
 * no reader, so the second overlay's belief and the loop's actual state simply
 * diverge, silently, and stay diverged until something else happens to write a
 * zero.
 *
 * ⚠ THIS IS THE SAME SHAPE AS `hurtPlayer(amount, lethal)` AND `FIRE.range`:
 * two places computing one thing, disagreeing in the state nobody tests. The
 * fix is the same shape too — delete one of the two authorities. Here that
 * means nobody calls `setTimeScale` directly any more; they declare that THEY
 * want the world frozen, by name, and this module owns the transitions:
 *
 *   * `setTimeScale(0)` fires on the **0 → 1** edge of the owner set;
 *   * `setTimeScale(1)` fires ONLY when the set **empties**.
 *
 * A named owner (rather than a bare counter) is what makes it idempotent in
 * BOTH directions: an overlay that acquires twice — a React strict-mode double
 * effect, a re-entrant open — still holds ONE claim, and a release from an
 * overlay that never acquired is a no-op instead of a stray unfreeze. That
 * matters because the asymmetric failure is unrecoverable in one direction:
 * a missed release leaves a live engine frozen at zero with no overlay on
 * screen, which is a dead game.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A `WeakMap`, KEYED ON THE ENGINE
 * ─────────────────────────────────────────────────────────────────────────────
 * The owner set is per ENGINE, not module-global: a route change disposes one
 * engine and builds another, and a stale owner id from the dead world must not
 * hold the new one frozen. Keyed weakly, so a disposed engine takes its set with
 * it and this module can never be the thing that retains a level.
 *
 * ⚠ Every entry point tolerates a null engine and a loop with no
 * `setTimeScale` — every dev scene and every test double is one of those, and an
 * overlay must not need to know which kind of engine it is mounted over.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MIGRATION NOTE
 * ─────────────────────────────────────────────────────────────────────────────
 * MIGRATION IS COMPLETE. `PhonePanel.jsx` and `PhoneCall.jsx` were built on this
 * from the start; `PauseMenu.jsx`, `Cutscene.jsx` and `ShopPanel.jsx` were
 * converted in the same round. All five owners are named here:
 * `'pause-menu'`, `'cutscene'`, `'shop-panel'`, `'phone-panel'`, `'phone-call'`.
 *
 * ⚠⚠ **THE PROOF IS A GREP, AND IT IS THE ONLY PROOF THERE IS.** The loop cannot
 * refuse a direct write, so ONE overlay calling `setTimeScale(1)` itself still
 * stomps every claim held here and the defect quietly comes back. There is no
 * runtime guard that could catch it and no test that would fail. The invariant
 * is therefore textual and must be checked as text:
 *
 *     grep -rn "setTimeScale" src/ui        # only this file may match
 *
 * ⚠ `PauseMenu.jsx` is the one whose conversion was not a straight swap: its
 * effect always ran and wrote `view === 'none' ? 1 : 0`, so it became an
 * acquire-or-release on that same condition. It has FOUR view values and only
 * TWO freeze states — keyed on the boolean, so PAUSED → MAP does not release and
 * re-acquire.
 *
 * @module ui/hud/freezeOwner
 */

/**
 * engine → the set of ids currently asking for a frozen world.
 * @type {WeakMap<object, Set<string>>}
 */
const OWNERS = new WeakMap();

/**
 * Declare that `ownerId` wants the world frozen. Freezes on the 0 → 1 edge and
 * does nothing on every subsequent call — acquiring twice is one claim.
 *
 * @param {any} engine Anything with a `.loop.setTimeScale`; null is a no-op.
 * @param {string} ownerId A stable name for the overlay, e.g. `'phone-panel'`.
 */
export function acquireFreeze(engine, ownerId) {
  if (!engine || typeof engine !== 'object' || !ownerId) return;
  let set = OWNERS.get(engine);
  if (!set) {
    set = new Set();
    OWNERS.set(engine, set);
  }
  const wasEmpty = set.size === 0;
  set.add(ownerId);
  // ⚠ The edge, not the membership: a second overlay opening over a frozen
  // world must not re-issue a zero, or a future `setTimeScale` with a side
  // effect (a fade, an audio duck) would fire twice for one freeze.
  if (wasEmpty) engine.loop?.setTimeScale?.(0);
}

/**
 * Give up `ownerId`'s claim. Restores time ONLY when the last claim goes — that
 * single line is the whole point of the module.
 *
 * @param {any} engine
 * @param {string} ownerId
 */
export function releaseFreeze(engine, ownerId) {
  if (!engine || typeof engine !== 'object' || !ownerId) return;
  const set = OWNERS.get(engine);
  // Not held: a no-op, never a stray unfreeze. A release that fires without a
  // matching acquire is how a double-cleanup restarts the world under a modal.
  if (!set?.delete(ownerId)) return;
  if (set.size > 0) return;
  OWNERS.delete(engine);
  engine.loop?.setTimeScale?.(1);
}

/**
 * How many overlays are holding this engine frozen. Debug/diagnostic only —
 * nothing in the HUD may branch on it, because "am I frozen" is a question with
 * one owner and that owner is this module.
 *
 * @param {any} engine
 * @returns {number}
 */
export function freezeOwnerCount(engine) {
  if (!engine || typeof engine !== 'object') return 0;
  return OWNERS.get(engine)?.size ?? 0;
}

/**
 * The hook form, so a caller cannot forget the release: the effect's CLEANUP is
 * the only path out, which is the same guarantee every overlay in this project
 * already relies on for `setTimeScale(1)`.
 *
 * ⚠⚠ `active` MUST BE A BOOLEAN, NEVER A PAYLOAD OBJECT. `'phone:state'` hands
 * the panel a NEW object after every order and `'phone:call'` hands a new one on
 * every shot; keying this effect on either would tear down and re-run the claim
 * on each republish, flicking the scale 0 → 1 → 0 for no reason and handing one
 * real step of the sim to a world the player is looking at through a modal.
 * `ShopPanel.jsx` documents the same rule against its own snapshot — this is
 * that rule, hoisted to where it cannot be forgotten.
 *
 * @param {any} engine
 * @param {string} ownerId
 * @param {boolean} active
 */
export function useFreeze(engine, ownerId, active) {
  const on = !!active;
  useEffect(() => {
    if (!on) return undefined;
    acquireFreeze(engine, ownerId);
    return () => releaseFreeze(engine, ownerId);
  }, [engine, ownerId, on]);
}
